import { HTMLAttributes, useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'

import { useListboxContext } from './ListboxContext'

import styles from './ListboxItem.module.scss'

export interface ListboxItemProps extends HTMLAttributes<HTMLLIElement> {
  children?: React.ReactNode
  optionId: string
}

const ListboxItem = ({
  children,
  optionId,
  className,
  ...props
}: ListboxItemProps) => {
  const context = useListboxContext()
  const ref = useRef<HTMLLIElement>(null)

  if (!context) {
    throw new Error('ListboxItem must be used within a Listbox provider.')
  }

  const isSelected = useMemo(
    () => context.selectedOptionIds.includes(optionId),
    [context.selectedOptionIds, optionId]
  )

  const isFocused = useMemo(
    () => context.focusedOptionId === optionId,
    [context.focusedOptionId, optionId]
  )

  useEffect(() => {
    if (isFocused) {
      ref.current?.focus()
    }
  }, [isFocused, ref])

  return (
    <li
      id={`${context.id}-${optionId}`}
      ref={ref}
      role='option'
      aria-selected={isSelected}
      tabIndex={isSelected || isFocused ? 0 : -1}
      onClick={() => context.onSelect(optionId)}
      {...props}
      className={clsx(
        styles.item,
        { [styles.selected]: isSelected },
        className
      )}
    >
      {children}
    </li>
  )
}

export default ListboxItem
